<script setup lang="ts">
import type { AddEntityType } from '~/components/orga/OrgaElement.vue'
import type { GridCol } from '~~/types/orgGrid'
import OrgaElement from '~/components/orga/OrgaElement.vue'
import DirectionEmptyEncart from '~/components/Direction/page/DirectionEmptyEncart.vue'
import DirectionDomaineColumn from '~/components/Direction/page/DirectionDomaineColumn.vue'
import DirectionOrgGridSkeleton from '~/components/Direction/page/DirectionOrgGridSkeleton.vue'

defineProps<{
  grid: GridCol[]
  directionLib: string
  isEditing: boolean
  isDirectionOrganisationEmpty: boolean
  canEditOrganigramOnCurrentView: boolean
  canAddDomaine: boolean
  canEditDomaineColumn: (domaineLib: string | undefined) => boolean
  emphasizeLimitedScope: boolean
  isReordering?: boolean
}>()

const emit = defineEmits<{
  (event: 'add-click', payload: { entityType: AddEntityType; context?: Record<string, string | undefined> }): void
  (event: 'modify-click', context?: Record<string, string | undefined>): void
  (event: 'mouseenter-col', col: GridCol): void
  (event: 'mouseleave-col'): void
  (event: 'reorder-move', payload: { groupId: string; keyName: string; fromIndex: number }): void
}>()
</script>

<template>
  <ClientOnly>
    <div
      class="columns-wrapper"
      :class="{
        'columns-wrapper--direction-empty': isDirectionOrganisationEmpty && !isEditing,
        'columns-wrapper--organisation-empty-edit': isDirectionOrganisationEmpty && isEditing,
      }"
    >
      <div
        v-if="isDirectionOrganisationEmpty && isEditing && canAddDomaine"
        class="direction-empty-edit-add-domaine-bar"
      >
        <OrgaElement
          status="add"
          add-entity-type="domaine"
          :add-context="{ directionLib: directionLib }"
          @add-click="emit('add-click', $event)"
        />
      </div>
      <TransitionGroup
        tag="div"
        name="domaine-shift"
        class="columns-grid"
        data-onboarding="direction-grid"
        :data-reorder-group="isReordering ? directionLib : undefined"
        :class="{
          'columns-grid--direction-empty': isDirectionOrganisationEmpty && !isEditing,
          'columns-grid--reorder': isReordering,
        }"
      >
        <DirectionEmptyEncart
          v-if="isDirectionOrganisationEmpty && !isEditing"
          key="empty-direction"
          :can-edit-organigram-on-current-view="canEditOrganigramOnCurrentView"
        />
        <DirectionDomaineColumn
          v-for="(col, colIndex) in grid"
          :key="col.header?.label || 'col-' + colIndex"
          :col="col"
          :col-index="colIndex"
          :direction-lib="directionLib"
          :is-editing="isEditing"
          :can-modify-domaine="canAddDomaine"
          :can-mutate-children="canEditDomaineColumn(col?.header?.label)"
          :emphasize-scope="emphasizeLimitedScope"
          :in-scope="canEditDomaineColumn(col?.header?.label)"
          :reorderable="isReordering"
          :reorder-index="colIndex"
          :reorder-total="grid.length"
          @reorder-move="emit('reorder-move', $event)"
          @add-click="emit('add-click', $event)"
          @modify-click="emit('modify-click', $event)"
          @mouseenter-col="emit('mouseenter-col', $event)"
          @mouseleave-col="emit('mouseleave-col')"
        />
        <Transition key="add-column" name="expand-edit">
          <div v-if="isEditing && canAddDomaine && !isDirectionOrganisationEmpty" class="col col--add">
            <div class="col-header col-header--add">
              <OrgaElement
                status="add"
                add-entity-type="domaine"
                :add-context="{ directionLib: directionLib }"
                @add-click="emit('add-click', $event)"
              />
            </div>
          </div>
        </Transition>
      </TransitionGroup>
    </div>
    <template #fallback>
      <DirectionOrgGridSkeleton />
    </template>
  </ClientOnly>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/orga/national-grid" as *;
@use "~/assets/scss/partials/orga/direction-empty-encart" as *;

.columns-grid--reorder {
  display: flex;
  align-items: flex-start;
}

.columns-grid--reorder > .col {
  flex: 1 1 11.25rem;
  min-width: 11.25rem;
  animation: none;
}

.domaine-shift-move {
  transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
}

@media (prefers-reduced-motion: reduce) {
  .domaine-shift-move {
    transition: none;
  }
}
</style>

<style lang="scss">
.org-reorder-ghost.col--animate,
.org-reorder-drag.col--animate {
  height: auto !important;
  background: transparent;
  box-shadow: none;
  animation: none;
}

.org-reorder-drag.col--animate {
  opacity: 1 !important;
}
</style>
